import type { Dispatch, SetStateAction } from 'react';
import api from '@crm-src/lib/api';
import { dealsApi, type Deal } from '@crm-src/lib/deals-api';
import { getLocalDateString } from '@crm-src/lib/date-utils';
import { toast } from '@/components/ui/use-toast';

import { defaultTerms, deriveDueDate } from './formData';
import { incrementDocumentNumber } from './documentNumber';
import { PRICE_LIST_PRICING_MODES, type PriceListPricingMode } from '@xop/dashboard-shared';

import type { ProductSelectionMode } from './products-table';

type SetState<T> = Dispatch<SetStateAction<T>>;

/**
 * The price breadcrumb a stored line carries beside its copied numbers: what
 * the book charged at the moment the line was filled (`listPriceCents`).
 * `buildInvoicePayload` sends the product rows verbatim, so the key comes back
 * on load and every loader has to hand it to the form — a reload that dropped
 * it would lose the "was this ever quoted off list?" answer.
 *
 * A line filled before 2026-09-13 may also carry `selectedPriceListItemId`,
 * which named a row in the retired price-list table. It is deliberately NOT
 * read back: those ids address a table nothing writes any more, and carrying a
 * dead pointer forward would make it look resolvable. The cents are the part
 * that still means something.
 *
 * Spread into the mapped line so an absent breadcrumb stays ABSENT (the same
 * shape `withProductMode` leaves behind, and what exactOptionalPropertyTypes
 * wants). `listPriceCents` is nullish-checked, never truthiness-checked: 0 is a
 * real list price (an `included` row).
 */
function priceListSnapshot(p: any): {
  listPriceCents?: number;
  pricingMode?: PriceListPricingMode;
  percentRate?: number;
} {
  const snapshot: { listPriceCents?: number; pricingMode?: PriceListPricingMode; percentRate?: number } = {};
  const cents = Number(p?.listPriceCents);
  if (p?.listPriceCents != null && Number.isFinite(cents)) snapshot.listPriceCents = cents;
  // HOW the line is priced (2026-09-13). A `percent` line must come back as
  // one, or the resolver stops following the order the moment the document is
  // reopened and the ESP line freezes at whatever it was on the last save.
  // Only the three modes the book knows are read back — a word the resolver
  // would trip on stays out.
  if ((PRICE_LIST_PRICING_MODES as readonly string[]).includes(p?.pricingMode)) {
    snapshot.pricingMode = p.pricingMode as PriceListPricingMode;
    const rate = Number(p?.percentRate);
    if (snapshot.pricingMode === 'percent' && p?.percentRate != null && Number.isFinite(rate)) {
      snapshot.percentRate = rate;
    }
  }
  return snapshot;
}

/**
 * Resolve the customer contact used to seed the generate form when LOADING an
 * existing document (edit / duplicate / convert).
 *
 * The whole form body is gated on `selectedContact`, so a loader that returns no
 * customer drops the user on a blank page. Two production shapes make a naive
 * `contacts.find(id)` miss:
 *   1. The `/customers` list is scoped to the viewer's own records AND capped at
 *      500 rows, so a doc the viewer can open (e.g. a salesperson who owns a quote
 *      via its `sales_person` label) may reference a customer that isn't in it.
 *   2. The customer record was DELETED after the doc was created — `customer_id`
 *      (`invoice.customer&.id`) comes back null, though the doc still carries the
 *      snapshot `customer_name` / `customer_company_name`. This is the "Edit does
 *      nothing on this specific quote" bug.
 *
 * So: prefer the live row from the list; else fall back to the doc's own snapshot
 * fields (id may legitimately be null). Returns null only when the doc carries no
 * customer identity at all — nothing to show.
 */
export function resolveFormCustomer(doc: any, contacts: any[]): any | null {
  const found = contacts.find((c) => c.id === doc.customer_id);
  if (found) return found;
  if (doc.customer_id || doc.customer_name || doc.customer_company_name) {
    return {
      id: doc.customer_id ?? null,
      full_name: doc.customer_name || '',
      company_name: doc.customer_company_name || '',
      email: doc.customer_email || '',
    };
  }
  return null;
}

/**
 * The reseller a document is ADDRESSED TO, when it is addressed to one rather
 * than to a customer (2026-09-13): `bill_to_reseller_id` with NO customer. That
 * is crm-api's own definition (`Invoice.reseller_issued_visible` carries the
 * same `customer_id IS NULL` guard, and `generate_reseller_document_number`
 * keys on it), so the command center reopens exactly the rows the reseller's
 * portal lists. A customer's document billed to its reseller has BOTH ids and
 * is not this — it reopens through `resolveFormCustomer`.
 */
export function addressedResellerIdOf(doc: any): string | null {
  if (doc?.customer_id || !doc?.bill_to_reseller_id) return null;
  return String(doc.bill_to_reseller_id);
}

interface CommonDeps {
  contacts: any[];
  users: any[];
  setIsLoadingInvoice: SetState<boolean>;
  setSelectedContact: SetState<any | null>;
  /** Restore the Bill To recipient (reseller / dealer) from the loaded
   * document's `bill_to_reseller_id` (2026-09-12). Optional: the duplication
   * loader does not pass it. */
  setBillToRecipient?: (resellerId: number | null, customer: any) => void;
  /** Put the form into "addressed to a reseller" mode for a document with no
   * customer (2026-09-15) — the form's ONE reseller loader, shared with the
   * `?resellerId=` deep link and the picker. `mintNumber` is true for a copy
   * (duplicate / convert) and false for an edit, which keeps its number;
   * `fillBillTo` is always false here — the document's own stored bill-to
   * has just been written into the form. Optional: the Otto draft loader
   * never addresses a reseller. */
  addressToReseller?: (
    resellerId: string,
    documentType: 'invoice' | 'quote',
    opts: { fillBillTo: boolean; mintNumber: boolean },
  ) => Promise<void>;
  setSelectedSalesPerson: SetState<any | null>;
  setSelectedDeal: SetState<Deal | null>;
  setFormData: SetState<any>;
  setPriceInputs: SetState<{ [productId: string]: string }>;
}

export interface LoadInvoiceDataDeps extends CommonDeps {
  invoiceId: string | null;
  setExistingScheduleId: SetState<string | null>;
  setEnableRecurring: SetState<boolean>;
  setRecurringFrequency: SetState<'weekly' | 'monthly' | 'quarterly' | 'yearly'>;
  setRecurringInterval: SetState<number>;
  setRecurringStartDate: SetState<string>;
  setRecurringDayOfWeek: SetState<number>;
  setRecurringDayOfMonth: SetState<number>;
}

export interface LoadQuoteForConversionDeps extends CommonDeps {
  quoteToConvert: string | null;
  setIsGeneratingInvoiceNumber: SetState<boolean>;
  generateDocumentNumber: (customer: any, documentType: 'invoice' | 'quote') => Promise<string>;
}

export interface LoadInvoiceForDuplicationDeps extends CommonDeps {
  duplicateFrom: string | null;
  setIsGeneratingInvoiceNumber: SetState<boolean>;
  generateDocumentNumber: (customer: any, documentType: 'invoice' | 'quote') => Promise<string>;
}

// Function to load invoice data for editing
export const loadInvoiceData = async (deps: LoadInvoiceDataDeps): Promise<void> => {
  const {
    invoiceId,
    contacts,
    users,
    setIsLoadingInvoice,
    setSelectedContact,
    setSelectedSalesPerson,
    setSelectedDeal,
    setFormData,
    setPriceInputs,
    setExistingScheduleId,
    setEnableRecurring,
    setRecurringFrequency,
    setRecurringInterval,
    setRecurringStartDate,
    setRecurringDayOfWeek,
    setRecurringDayOfMonth,
  } = deps;

  if (!invoiceId) return;

  try {
    setIsLoadingInvoice(true);

    const response = await api.get(`/invoices/${invoiceId}`);
    const invoiceData = response.data.data || response.data.invoice || response.data;


    // Addressed to a RESELLER (no customer): the form's reseller loader owns
    // that state (below, once the form is populated). Otherwise find and set
    // the customer contact. resolveFormCustomer falls back to the doc's
    // snapshot when the customer is out of scope OR was deleted (customer_id
    // null) — otherwise the form body (gated on selectedContact) never renders
    // and Edit appears to do nothing. See resolveFormCustomer.
    const addressedResellerId = addressedResellerIdOf(invoiceData);
    const customer = addressedResellerId ? null : resolveFormCustomer(invoiceData, contacts);
    if (customer) {
      setSelectedContact(customer);
    }

    // Find and set the sales person
    if (invoiceData.sales_person && users.length > 0) {
      const salesPerson = users.find(u => {
        const fullName = u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim();
        return fullName === invoiceData.sales_person;
      });
      if (salesPerson) {
        setSelectedSalesPerson(salesPerson);
      }
    }

    // Load the customer's deal
    if (invoiceData.customer_deal_id) {
      try {
        const dealResponse = await dealsApi.getDeal(invoiceData.customer_deal_id.toString());
        setSelectedDeal(dealResponse);
      } catch (error) {
        console.error('⚠️ Could not load deal via API, using fallback:', error);
        // Deal not accessible (e.g. salesperson's customer assignment changed)
        // Create a minimal deal object so the form can still submit
        setSelectedDeal({
          id: invoiceData.customer_deal_id.toString(),
          customer_id: invoiceData.customer_id?.toString() || '',
          customer: { id: '', customer_id: '', full_name: '', company_name: '' },
          title: invoiceData.customer_deal_title || 'Deal',
          amount: 0,
          deal_type: '',
          total_with_maintenance: 0,
          is_recurring: false,
          has_invoices: true,
          total_invoiced_amount: 0,
          remaining_amount: 0,
          created_at: '',
          updated_at: '',
        } as Deal);
      }
    }

    // Populate form data
    setFormData({
      customer_id: invoiceData.customer_id || null,
      customer_deal_id: invoiceData.customer_deal_id || null,
      document_type: invoiceData.document_type || 'invoice',
      invoice_number: invoiceData.invoice_number || '',
      subject: invoiceData.subject || '',
      issue_date: invoiceData.issue_date || getLocalDateString(),
      due_date: invoiceData.due_date || deriveDueDate(invoiceData.issue_date || getLocalDateString()),
      service_from: invoiceData.service_from || '',
      service_to: invoiceData.service_to || '',
      po_number: invoiceData.po_number || 'verbal',
      sales_person: invoiceData.sales_person || '',
      terms: invoiceData.terms || 'Due upon receipt of Invoice',

      // Bill To Address
      bill_to_company: invoiceData.bill_to_company || '',
      bill_to_address: invoiceData.bill_to_address || '',
      bill_to_city: invoiceData.bill_to_city || '',
      bill_to_state: invoiceData.bill_to_state || '',
      bill_to_zip: invoiceData.bill_to_zip || '',
      bill_to_country: invoiceData.bill_to_country || 'U.S.A',

      // Ship To Address
      ship_to_company: invoiceData.ship_to_company || '',
      ship_to_address: invoiceData.ship_to_address || '',
      ship_to_city: invoiceData.ship_to_city || '',
      ship_to_state: invoiceData.ship_to_state || '',
      ship_to_zip: invoiceData.ship_to_zip || '',
      ship_to_country: invoiceData.ship_to_country || 'U.S.A',
      bill_to_contact_id: invoiceData.bill_to_contact_id ?? null,
      bill_to_email: invoiceData.bill_to_email || '',
      bill_to_contact_name: invoiceData.bill_to_contact_name || '',
      ship_to_contact_id: invoiceData.ship_to_contact_id ?? null,
      ship_to_email: invoiceData.ship_to_email || '',
      ship_to_contact_name: invoiceData.ship_to_contact_name || '',

      // Products - convert all numeric values to numbers and preserve selection mode
      products: invoiceData.products && Array.isArray(invoiceData.products) && invoiceData.products.length > 0
        ? invoiceData.products.map((p: any, index: number) => ({
            id: (index + 1).toString(),
            partNumber: p.partNumber || p.part_number || '',
            description: p.description || '',
            seats: Number(p.seats) || 1,
            chargePerSeat: Number(p.chargePerSeat || p.charge_per_seat) || 0,
            amount: Number(p.amount) || 0,
            selectionMode: (p.selectionMode || 'manual') as ProductSelectionMode,
            selectedProductId: p.selectedProductId || undefined,
            selectedFeatureId: p.selectedFeatureId || undefined,
            ...priceListSnapshot(p),
            excludeFromDiscount: p.excludeFromDiscount || false  // Load discount exclusion flag
          }))
        : [{
            id: '1',
            partNumber: '',
            description: '',
            seats: 1,
            chargePerSeat: 0,
            amount: 0,
            selectionMode: 'manual' as const,
            excludeFromDiscount: false  // Default: apply discounts
          }],

      // Discount / tax INPUTS only. sub_total / tax_amount /
      // total_amount / balance_due are derived from products + these by
      // the form's `totals` memo, so loading them here is unnecessary
      // (and was a source of stale totals on edit).
      market_discount: Number(invoiceData.market_discount) || 0,
      reseller_discount: Number(invoiceData.reseller_discount) || 0,
      additional_discount_amount: Number(invoiceData.additional_discount_amount) || 0,
      additional_discount_percentage: Number(invoiceData.additional_discount_percentage) || 0,
      tax_percentage: Number(invoiceData.tax_percentage) || 0
    } as any);

    // Initialize price inputs with formatted values
    if (invoiceData.products && Array.isArray(invoiceData.products) && invoiceData.products.length > 0) {
      const initialPriceInputs: { [key: string]: string } = {};
      invoiceData.products.forEach((p: any, index: number) => {
        const price = Number(p.chargePerSeat || p.charge_per_seat) || 0;
        if (price > 0) {
          initialPriceInputs[(index + 1).toString()] = price.toFixed(2);
        }
      });
      setPriceInputs(initialPriceInputs);
    }

    // Load existing recurring schedule if any (only for invoices, not quotes)
    if (invoiceData.document_type !== 'quote') {
      try {
        const { invoiceSchedulesApi } = await import('@crm-src/lib/invoice-schedules-api');
        const scheduleResponse = await invoiceSchedulesApi.getBySourceInvoice(invoiceId);

        if (scheduleResponse.success && scheduleResponse.data) {
          const schedule = scheduleResponse.data;

          // Store existing schedule ID for updates
          setExistingScheduleId(schedule.id);

          // Enable recurring toggle and populate settings
          setEnableRecurring(true);
          setRecurringFrequency(schedule.frequency as 'weekly' | 'monthly' | 'quarterly' | 'yearly');
          setRecurringInterval(schedule.interval || 1);
          // Extract just the date part (YYYY-MM-DD) from the ISO date string
          const startDateStr = schedule.start_date ? schedule.start_date.split('T')[0] : '';
          setRecurringStartDate(startDateStr);
          setRecurringDayOfWeek(schedule.day_of_week || 1);
          setRecurringDayOfMonth(schedule.day_of_month || 1);
        }
      } catch (error) {
        console.error('Error loading existing schedule:', error);
        // Non-fatal error - just continue without schedule data
      }
    }

    // Restore WHO the document is billed to. (This used to sit in the catch
    // below — the one path that never had a document to restore it from.)
    deps.setBillToRecipient?.(invoiceData.bill_to_reseller_id ?? null, customer);
    // An edit keeps its stored number and bill-to; the loader only names the
    // reseller so the picker and the banner show them.
    if (addressedResellerId) {
      await deps.addressToReseller?.(addressedResellerId, invoiceData.document_type || 'invoice', {
        fillBillTo: false,
        mintNumber: false,
      });
    }

  } catch (error) {
    console.error('❌ Error loading invoice data:', error);
    toast({
      title: 'Error',
      description: 'Failed to load invoice data',
      variant: 'destructive'
    });
  } finally {
    setIsLoadingInvoice(false);
  }
};

// Function to load quote data for conversion to invoice
export const loadQuoteDataForConversion = async (deps: LoadQuoteForConversionDeps): Promise<void> => {
  const {
    quoteToConvert,
    contacts,
    users,
    setIsLoadingInvoice,
    setSelectedContact,
    setSelectedSalesPerson,
    setSelectedDeal,
    setFormData,
    setPriceInputs,
    setIsGeneratingInvoiceNumber,
    generateDocumentNumber,
  } = deps;

  if (!quoteToConvert) return;

  try {
    setIsLoadingInvoice(true);

    const response = await api.get(`/invoices/${quoteToConvert}`);
    const quoteData = response.data.data || response.data.invoice || response.data;


    // Addressed to a RESELLER (no customer) — the form's reseller loader takes
    // over below; else find and set the customer contact (snapshot fallback —
    // see resolveFormCustomer).
    const addressedResellerId = addressedResellerIdOf(quoteData);
    const customer = addressedResellerId ? null : resolveFormCustomer(quoteData, contacts);
    if (customer) {
      setSelectedContact(customer);
    }

    // Find and set the sales person
    if (quoteData.sales_person && users.length > 0) {
      const salesPerson = users.find(u => {
        const fullName = u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim();
        return fullName === quoteData.sales_person;
      });
      if (salesPerson) {
        setSelectedSalesPerson(salesPerson);
      }
    }

    // Load the customer's deal
    if (quoteData.customer_deal_id) {
      try {
        const dealResponse = await dealsApi.getDeal(quoteData.customer_deal_id.toString());
        setSelectedDeal(dealResponse);
      } catch (error) {
        console.error('Error loading deal:', error);
      }
    }

    // Populate form data - IMPORTANT: Clear invoice_number so new one gets generated
    setFormData({
      customer_id: quoteData.customer_id || null,
      customer_deal_id: quoteData.customer_deal_id || null,
      document_type: 'invoice', // Force invoice type
      invoice_number: '', // Clear quote number - will generate new invoice number
      subject: quoteData.subject || '',
      issue_date: getLocalDateString(), // Use today's date
      due_date: deriveDueDate(getLocalDateString()), // 30 days from the issue date
      po_number: quoteData.po_number || 'verbal',
      sales_person: quoteData.sales_person || '',
      terms: 'Due upon receipt of Invoice', // Invoice terms, not quote terms

      // Copy all address data from quote
      bill_to_company: quoteData.bill_to_company || '',
      bill_to_address: quoteData.bill_to_address || '',
      bill_to_city: quoteData.bill_to_city || '',
      bill_to_state: quoteData.bill_to_state || '',
      bill_to_zip: quoteData.bill_to_zip || '',
      bill_to_country: quoteData.bill_to_country || 'U.S.A',

      ship_to_company: quoteData.ship_to_company || '',
      ship_to_address: quoteData.ship_to_address || '',
      ship_to_city: quoteData.ship_to_city || '',
      ship_to_state: quoteData.ship_to_state || '',
      ship_to_zip: quoteData.ship_to_zip || '',
      ship_to_country: quoteData.ship_to_country || 'U.S.A',
      bill_to_contact_id: quoteData.bill_to_contact_id ?? null,
      bill_to_email: quoteData.bill_to_email || '',
      bill_to_contact_name: quoteData.bill_to_contact_name || '',
      ship_to_contact_id: quoteData.ship_to_contact_id ?? null,
      ship_to_email: quoteData.ship_to_email || '',
      ship_to_contact_name: quoteData.ship_to_contact_name || '',

      // Copy all products from quote and preserve selection mode
      products: quoteData.products && Array.isArray(quoteData.products) && quoteData.products.length > 0
        ? quoteData.products.map((p: any, index: number) => ({
            id: (index + 1).toString(),
            partNumber: p.partNumber || p.part_number || '',
            description: p.description || '',
            seats: Number(p.seats) || 1,
            chargePerSeat: Number(p.chargePerSeat || p.charge_per_seat) || 0,
            amount: Number(p.amount) || 0,
            selectionMode: (p.selectionMode || 'manual') as ProductSelectionMode,
            selectedProductId: p.selectedProductId || undefined,
            selectedFeatureId: p.selectedFeatureId || undefined,
            ...priceListSnapshot(p),
            excludeFromDiscount: p.excludeFromDiscount || false  // Copy discount exclusion flag from quote
          }))
        : [{
            id: '1',
            partNumber: '',
            description: '',
            seats: 1,
            chargePerSeat: 0,
            amount: 0,
            selectionMode: 'manual' as const,
            excludeFromDiscount: false  // Default: apply discounts
          }],

      // Discount / tax INPUTS only — totals are derived (see above).
      market_discount: Number(quoteData.market_discount) || 0,
      reseller_discount: Number(quoteData.reseller_discount) || 0,
      additional_discount_amount: Number(quoteData.additional_discount_amount) || 0,
      additional_discount_percentage: Number(quoteData.additional_discount_percentage) || 0,
      tax_percentage: Number(quoteData.tax_percentage) || 0
    } as any);

    // Initialize price inputs with formatted values
    if (quoteData.products && Array.isArray(quoteData.products) && quoteData.products.length > 0) {
      const initialPriceInputs: { [key: string]: string } = {};
      quoteData.products.forEach((p: any, index: number) => {
        const price = Number(p.chargePerSeat || p.charge_per_seat) || 0;
        if (price > 0) {
          initialPriceInputs[(index + 1).toString()] = price.toFixed(2);
        }
      });
      setPriceInputs(initialPriceInputs);
    }


    // Restore WHO the document is billed to (see loadInvoiceData).
    deps.setBillToRecipient?.(quoteData.bill_to_reseller_id ?? null, customer);

    // Generate new invoice number for the invoice. A quote addressed to a
    // reseller converts into an invoice addressed to the same reseller, numbered
    // from THEIR `<code>-0000-` sequence; a customer's needs a real customer id
    // (the snapshot fallback can carry a null id for a deleted customer).
    if (addressedResellerId) {
      await deps.addressToReseller?.(addressedResellerId, 'invoice', { fillBillTo: false, mintNumber: true });
    } else if (customer?.id) {
      try {
        setIsGeneratingInvoiceNumber(true);
        const newInvoiceNumber = await generateDocumentNumber(customer, 'invoice');
        setFormData((prev: any) => ({ ...prev, invoice_number: newInvoiceNumber }));
      } catch (error) {
        console.error('Error generating invoice number:', error);
      } finally {
        setIsGeneratingInvoiceNumber(false);
      }
    }
  } catch (error) {
    console.error('❌ Error loading quote data for conversion:', error);
    toast({
      title: 'Error',
      description: 'Failed to load quote data',
      variant: 'destructive'
    });
  } finally {
    setIsLoadingInvoice(false);
  }
};

// Function to load invoice data for duplication
export const loadInvoiceDataForDuplication = async (deps: LoadInvoiceForDuplicationDeps): Promise<void> => {
  const {
    duplicateFrom,
    contacts,
    users,
    setIsLoadingInvoice,
    setSelectedContact,
    setSelectedSalesPerson,
    setSelectedDeal,
    setFormData,
    setPriceInputs,
    setIsGeneratingInvoiceNumber,
    generateDocumentNumber,
  } = deps;

  if (!duplicateFrom) return;

  try {
    setIsLoadingInvoice(true);

    const response = await api.get(`/invoices/${duplicateFrom}`);
    const invoiceData = response.data.data || response.data.invoice || response.data;


    // Addressed to a RESELLER (no customer) — the form's reseller loader takes
    // over below. Else find and set the customer contact (snapshot fallback —
    // see resolveFormCustomer). Without it, duplicating a doc whose customer is
    // out of scope or deleted drops the user on a blank create page (the whole
    // body gates on `selectedContact`).
    const addressedResellerId = addressedResellerIdOf(invoiceData);
    const customer = addressedResellerId ? null : resolveFormCustomer(invoiceData, contacts);
    if (customer) {
      setSelectedContact(customer);
    }

    // Find and set the sales person
    if (invoiceData.sales_person && users.length > 0) {
      const salesPerson = users.find(u => {
        const fullName = u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim();
        return fullName === invoiceData.sales_person;
      });
      if (salesPerson) {
        setSelectedSalesPerson(salesPerson);
      }
    }

    // Load the customer's deal
    if (invoiceData.customer_deal_id) {
      try {
        const dealResponse = await dealsApi.getDeal(invoiceData.customer_deal_id.toString());
        setSelectedDeal(dealResponse);
      } catch (error) {
        console.error('Error loading deal:', error);
      }
    }

    // Populate form data - IMPORTANT: Clear invoice_number so new one gets generated
    // Keep the same document_type (invoice or quote)
    setFormData({
      customer_id: invoiceData.customer_id || null,
      customer_deal_id: invoiceData.customer_deal_id || null,
      document_type: invoiceData.document_type || 'invoice', // Preserve document type
      invoice_number: '', // Clear invoice number - will generate new one
      subject: invoiceData.subject || '',
      issue_date: getLocalDateString(), // Use today's date
      due_date: deriveDueDate(getLocalDateString()), // 30 days from the issue date
      // Carry the service period over — a duplicate bills the same window
      // until the user edits it (mirrors edit-mode's loadInvoiceData).
      service_from: invoiceData.service_from || '',
      service_to: invoiceData.service_to || '',
      po_number: invoiceData.po_number || 'verbal',
      sales_person: invoiceData.sales_person || '',
      terms: invoiceData.terms || 'Due upon receipt of Invoice',

      // Copy all address data from original invoice
      bill_to_company: invoiceData.bill_to_company || '',
      bill_to_address: invoiceData.bill_to_address || '',
      bill_to_city: invoiceData.bill_to_city || '',
      bill_to_state: invoiceData.bill_to_state || '',
      bill_to_zip: invoiceData.bill_to_zip || '',
      bill_to_country: invoiceData.bill_to_country || 'U.S.A',

      ship_to_company: invoiceData.ship_to_company || '',
      ship_to_address: invoiceData.ship_to_address || '',
      ship_to_city: invoiceData.ship_to_city || '',
      ship_to_state: invoiceData.ship_to_state || '',
      ship_to_zip: invoiceData.ship_to_zip || '',
      ship_to_country: invoiceData.ship_to_country || 'U.S.A',
      bill_to_contact_id: invoiceData.bill_to_contact_id ?? null,
      bill_to_email: invoiceData.bill_to_email || '',
      bill_to_contact_name: invoiceData.bill_to_contact_name || '',
      ship_to_contact_id: invoiceData.ship_to_contact_id ?? null,
      ship_to_email: invoiceData.ship_to_email || '',
      ship_to_contact_name: invoiceData.ship_to_contact_name || '',

      // Copy all products from original invoice and preserve selection mode
      products: invoiceData.products && Array.isArray(invoiceData.products) && invoiceData.products.length > 0
        ? invoiceData.products.map((p: any, index: number) => ({
            id: (index + 1).toString(),
            partNumber: p.partNumber || p.part_number || '',
            description: p.description || '',
            seats: Number(p.seats) || 1,
            chargePerSeat: Number(p.chargePerSeat || p.charge_per_seat) || 0,
            amount: Number(p.amount) || 0,
            selectionMode: (p.selectionMode || 'manual') as ProductSelectionMode,
            selectedProductId: p.selectedProductId || undefined,
            selectedFeatureId: p.selectedFeatureId || undefined,
            ...priceListSnapshot(p),
            excludeFromDiscount: p.excludeFromDiscount || false  // Copy discount exclusion flag
          }))
        : [{
            id: '1',
            partNumber: '',
            description: '',
            seats: 1,
            chargePerSeat: 0,
            amount: 0,
            selectionMode: 'manual' as const,
            excludeFromDiscount: false  // Default: apply discounts
          }],

      // Discount / tax INPUTS only — totals are derived (see above).
      market_discount: Number(invoiceData.market_discount) || 0,
      reseller_discount: Number(invoiceData.reseller_discount) || 0,
      additional_discount_amount: Number(invoiceData.additional_discount_amount) || 0,
      additional_discount_percentage: Number(invoiceData.additional_discount_percentage) || 0,
      tax_percentage: Number(invoiceData.tax_percentage) || 0
    } as any);

    // Initialize price inputs with formatted values
    if (invoiceData.products && Array.isArray(invoiceData.products) && invoiceData.products.length > 0) {
      const initialPriceInputs: { [key: string]: string } = {};
      invoiceData.products.forEach((p: any, index: number) => {
        const price = Number(p.chargePerSeat || p.charge_per_seat) || 0;
        if (price > 0) {
          initialPriceInputs[(index + 1).toString()] = price.toFixed(2);
        }
      });
      setPriceInputs(initialPriceInputs);
    }


    // Mint a FRESH number for the duplicate — it must never be left empty.
    // A document addressed to a reseller copies to the SAME reseller and takes
    // the next `<code>-0000-NNN` from their sequence (the form's reseller
    // loader). Otherwise prefer the customer-scoped generator (queries the
    // true next sequence, and uses the customer's real reseller code when the
    // contact is loaded); if the customer can't be resolved to a real id
    // (deleted / out-of-scope → the snapshot carries a null id), fall back to
    // incrementing the SOURCE number, which preserves its reseller + customer
    // segments. See documentNumber.ts.
    if (addressedResellerId) {
      await deps.addressToReseller?.(
        addressedResellerId,
        (invoiceData.document_type || 'invoice') as 'invoice' | 'quote',
        { fillBillTo: false, mintNumber: true },
      );
    } else {
      const documentType = (invoiceData.document_type || 'invoice') as 'invoice' | 'quote';
      setIsGeneratingInvoiceNumber(true);
      try {
        let newInvoiceNumber = customer?.id
          ? await generateDocumentNumber(customer, documentType)
          : '';
        if (!newInvoiceNumber) {
          newInvoiceNumber = incrementDocumentNumber(invoiceData.invoice_number, documentType);
        }
        if (newInvoiceNumber) {
          setFormData((prev: any) => ({ ...prev, invoice_number: newInvoiceNumber }));
        }
      } catch (error) {
        console.error('Error generating invoice number:', error);
        // Even on a generator failure, still give the duplicate a number.
        const fallback = incrementDocumentNumber(invoiceData.invoice_number, documentType);
        if (fallback) setFormData((prev: any) => ({ ...prev, invoice_number: fallback }));
      } finally {
        setIsGeneratingInvoiceNumber(false);
      }
    }
  } catch (error) {
    console.error('❌ Error loading invoice data for duplication:', error);
    toast({
      title: 'Error',
      description: 'Failed to load invoice data',
      variant: 'destructive'
    });
  } finally {
    setIsLoadingInvoice(false);
  }
};

/* ── Otto Agent draft hydration ─────────────────────────────────────────────
 * The agentic Staff Otto ("Otto Agent") resolves a customer + deal + contacts
 * + line items and hands the form a fully-formed DRAFT so the human only has to
 * review + Save. Unlike edit/duplicate/convert, there is NO source document to
 * fetch (Otto never saved one) — the draft is built in-memory and passed via a
 * one-shot sessionStorage handoff (`?draft=<nonce>`). This loader is
 * `loadInvoiceDataForDuplication` MINUS the source `GET /invoices/:id`: it still
 * resolves the real customer + deal (both records Otto looked up) so the form's
 * `selectedContact`/`selectedDeal` gate opens and the Generate/Save button
 * enables. See docs/otto-agent.md + docs/crm.md. */

export interface OttoInvoiceDraftProduct {
  partNumber?: string;
  description: string;
  seats: number;
  chargePerSeat: number;
  amount?: number;
  selectionMode?: ProductSelectionMode;
  selectedProductId?: number;
  selectedFeatureId?: number;
  listPriceCents?: number;
  pricingMode?: PriceListPricingMode;
  percentRate?: number;
  excludeFromDiscount?: boolean;
}

export interface OttoInvoiceDraft {
  /** Real, in-scope customer id (Otto resolved it via `find_customer`). */
  customer_id: number;
  /** A deal belonging to that customer (Otto resolved it via `get_customer_deals`). */
  customer_deal_id: number;
  bill_to_contact_id?: number | null;
  ship_to_contact_id?: number | null;
  document_type?: 'invoice' | 'quote';
  subject?: string;
  issue_date?: string;
  due_date?: string;
  po_number?: string;
  terms?: string;
  /** Snapshot fallbacks so `resolveFormCustomer` renders even if the customer is
   * outside the capped `/customers` list. */
  customer_name?: string;
  customer_company_name?: string;
  customer_email?: string;
  bill_to_company?: string;
  bill_to_address?: string;
  bill_to_city?: string;
  bill_to_state?: string;
  bill_to_zip?: string;
  bill_to_country?: string;
  products: OttoInvoiceDraftProduct[];
  market_discount?: number;
  reseller_discount?: number;
  additional_discount_amount?: number;
  additional_discount_percentage?: number;
  tax_percentage?: number;
}

/**
 * Validate an untrusted Otto draft (it came off the gateway). Throws with a
 * clear message when it's missing what the form needs to be save-ready: a real
 * customer + deal, and at least one line item with a description + a price.
 * Returns the narrowed draft on success.
 */
export function validateOttoInvoiceDraft(value: unknown): OttoInvoiceDraft {
  const d = value as Partial<OttoInvoiceDraft> | null;
  if (!d || typeof d !== 'object') throw new Error('Otto draft is not an object');
  if (!Number.isFinite(Number(d.customer_id))) throw new Error('Otto draft is missing a customer');
  if (!Number.isFinite(Number(d.customer_deal_id))) throw new Error('Otto draft is missing a deal');
  const products = Array.isArray(d.products) ? d.products : [];
  const usable = products.filter((p) => (p?.description ?? '').trim().length > 0);
  if (usable.length === 0) throw new Error('Otto draft has no line items with a description');
  return d as OttoInvoiceDraft;
}

export interface LoadInvoiceFromDraftDeps extends CommonDeps {
  draft: OttoInvoiceDraft;
  documentType: 'invoice' | 'quote';
  setIsGeneratingInvoiceNumber: SetState<boolean>;
  generateDocumentNumber: (customer: any, documentType: 'invoice' | 'quote') => Promise<string>;
}

export const loadInvoiceDataFromDraft = async (deps: LoadInvoiceFromDraftDeps): Promise<void> => {
  const {
    draft,
    documentType,
    contacts,
    setIsLoadingInvoice,
    setSelectedContact,
    setSelectedDeal,
    setFormData,
    setPriceInputs,
    setIsGeneratingInvoiceNumber,
    generateDocumentNumber,
  } = deps;

  if (!draft) return;

  try {
    setIsLoadingInvoice(true);

    // 1. Resolve the customer: prefer the live row from the capped list; else
    // fetch the real record by id (Otto resolved a real, in-scope id) so we get
    // the company name + contacts; else fall back to the draft's own snapshot.
    let customer: any = contacts.find((c) => c.id === draft.customer_id) ?? null;
    if (!customer && draft.customer_id) {
      try {
        const resp = await api.get(`/customers/${draft.customer_id}`);
        const fetched = resp.data?.data ?? resp.data?.customer ?? resp.data;
        if (fetched && fetched.id != null) customer = fetched;
      } catch {
        /* fall through to the snapshot below */
      }
    }
    if (!customer) {
      customer = resolveFormCustomer(
        {
          customer_id: draft.customer_id,
          customer_name: draft.customer_name,
          customer_company_name: draft.customer_company_name,
          customer_email: draft.customer_email,
        },
        [],
      );
    }
    if (customer) {
      setSelectedContact(customer);
    }

    // 2. Load the deal (the Generate/Save button gates on `selectedDeal`). If it
    // 403s (own-scope) or 404s, fall back to a minimal synthetic Deal so the
    // form is still submittable — the server re-validates the id on create.
    if (draft.customer_deal_id) {
      try {
        const deal = await dealsApi.getDeal(String(draft.customer_deal_id));
        setSelectedDeal(deal);
      } catch {
        setSelectedDeal({
          id: String(draft.customer_deal_id),
          customer_id: String(draft.customer_id),
        } as unknown as Deal);
      }
    }

    // 3. Map the draft into the form state.
    const products = (draft.products?.length
      ? draft.products
      : [{ description: '', seats: 1, chargePerSeat: 0 }]
    ).map((p, index) => {
      const seats = Number(p.seats) || 1;
      const chargePerSeat = Number(p.chargePerSeat) || 0;
      return {
        id: (index + 1).toString(),
        partNumber: p.partNumber || '',
        description: p.description || '',
        seats,
        chargePerSeat,
        amount: Number(p.amount ?? seats * chargePerSeat) || 0,
        selectionMode: (p.selectionMode || 'manual') as ProductSelectionMode,
        selectedProductId: p.selectedProductId || undefined,
        selectedFeatureId: p.selectedFeatureId || undefined,
        ...priceListSnapshot(p),
        excludeFromDiscount: p.excludeFromDiscount || false,
      };
    });

    const issueDate = draft.issue_date || getLocalDateString();
    setFormData({
      customer_id: draft.customer_id ?? null,
      customer_deal_id: draft.customer_deal_id ?? null,
      document_type: documentType,
      invoice_number: '', // minted below
      subject: draft.subject || '',
      issue_date: issueDate,
      due_date: draft.due_date || deriveDueDate(issueDate),
      service_from: '',
      service_to: '',
      po_number: draft.po_number || 'verbal',
      sales_person: '', // server forces the acting rep for non-admins
      terms: draft.terms || defaultTerms(documentType),

      bill_to_company: draft.bill_to_company || customer?.company_name || '',
      bill_to_address: draft.bill_to_address || '',
      bill_to_city: draft.bill_to_city || '',
      bill_to_state: draft.bill_to_state || '',
      bill_to_zip: draft.bill_to_zip || '',
      bill_to_country: draft.bill_to_country || 'U.S.A',

      ship_to_company: '',
      ship_to_address: '',
      ship_to_city: '',
      ship_to_state: '',
      ship_to_zip: '',
      ship_to_country: 'U.S.A',

      bill_to_contact_id: draft.bill_to_contact_id ?? null,
      bill_to_email: '',
      bill_to_contact_name: '',
      ship_to_contact_id: draft.ship_to_contact_id ?? null,
      ship_to_email: '',
      ship_to_contact_name: '',

      products,

      market_discount: Number(draft.market_discount) || 0,
      reseller_discount: Number(draft.reseller_discount) || 0,
      additional_discount_amount: Number(draft.additional_discount_amount) || 0,
      additional_discount_percentage: Number(draft.additional_discount_percentage) || 0,
      tax_percentage: Number(draft.tax_percentage) || 0,
    } as any);

    const initialPriceInputs: { [key: string]: string } = {};
    products.forEach((p) => {
      if (p.chargePerSeat > 0) initialPriceInputs[p.id] = p.chargePerSeat.toFixed(2);
    });
    setPriceInputs(initialPriceInputs);

    // 4. Mint a fresh document number (this is a NEW create).
    setIsGeneratingInvoiceNumber(true);
    try {
      const number = customer?.id ? await generateDocumentNumber(customer, documentType) : '';
      if (number) setFormData((prev: any) => ({ ...prev, invoice_number: number }));
    } catch (error) {
      console.error('Error generating document number for Otto draft:', error);
    } finally {
      setIsGeneratingInvoiceNumber(false);
    }
  } catch (error) {
    console.error('❌ Error loading Otto invoice draft:', error);
    toast({
      title: 'Error',
      description: 'Failed to load the Otto draft',
      variant: 'destructive',
    });
  } finally {
    setIsLoadingInvoice(false);
  }
};
